import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import classNames from 'classnames/bind';
import { memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SortAscIcon, SortDescIcon } from '~/assets/icons';
import { tzDayjs } from '~/utils/helper';
import styles from '../Alert.module.scss';
import { SEARCH_TIMEOUT } from '~/utils/constants/common';

const cx = classNames.bind(styles);

type Props = {
  alerts: any[];
  form: any;
  pageSize: number;
  setRefreshData: (refresh: boolean) => void;
  onDetail?: (row: any) => void;
};

const AlertTable = (props: Props) => {
  const { t } = useTranslation('admin/alert');

  const searchTimeout = useRef<any>(null);

  const handleSort = (field: string, type: 'asc' | 'desc') => {
    handleSelectedOptions('order_by', { field, type });
  };

  const getAlertObject = (alertSubject: any) => {
    if (alertSubject === 1) return t('alert_subject_options.warning');
    if (alertSubject === 2) return t('alert_subject_options.device');
    if (alertSubject === 3) return t('alert_subject_options.user');
    if (alertSubject === 4) return t('alert_subject_options.agentDistributor');

    return '';
  };

  const handleSelectedOptions = (field: string, value: any) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      props.form.setFieldValue(field, value);
    }, SEARCH_TIMEOUT);
  };

  return (
    <TableContainer
      sx={{
        border: '1px solid #D9D9D9',
        borderRadius: '1px',
        maxHeight: '100vh',
      }}
    >
      <Table stickyHeader>
        <TableHead className='bg-[#005F73]'>
          <TableRow>
            <TableCell className={cx('w-[70px]', 'table-header')} align='center'>
              {t('no')}
            </TableCell>

            <TableCell className='text-white border-l border-white w-[197px]'>
              <Box className={cx('header-title-container')}>
                <Typography className={cx('header-title')}>{t('alert_lever')}</Typography>

                {props.form.values.order_by?.type === 'asc' && props.form.values.order_by?.field === 'alert_lever' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('alert_lever', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('alert_lever', 'asc');
                    }}
                  />
                )}
              </Box>
            </TableCell>

            <TableCell className='text-white border-l border-white w-[213px]'>
              <Box className={cx('header-title-container')}>
                <Box>{t('alert_subject')}</Box>
                {props.form.values.order_by?.type === 'asc' && props.form.values.order_by?.field === 'alert_subject' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('alert_subject', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('alert_subject', 'asc');
                    }}
                  />
                )}
              </Box>
            </TableCell>

            <TableCell className='text-white border-l border-white w-[186px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('alert_object')}</Box>
                {props.form.values.order_by?.type === 'asc' && props.form.values.order_by?.field === 'alert_object' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('alert_object', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('alert_object', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.alert_object}
                placeholder={t('longSearch')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('alert_object', e.target.value)}
              />
            </TableCell>

            <TableCell className='text-white border-l border-white w-[507px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('content')}</Box>
                {props.form.values.order_by?.type === 'asc' && props.form.values.order_by?.field === 'content' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('content', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('content', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.content}
                placeholder={t('longSearch')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('content', e.target.value)}
              />
            </TableCell>

            <TableCell className='text-white border-l border-white w-[188px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('created_at')}</Box>
                {props.form.values.order_by?.type === 'asc' && props.form.values.order_by?.field === 'created_at' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('created_at', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('created_at', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.created_at}
                placeholder={t('longSearch')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('created_at', e.target.value)}
              />
            </TableCell>

            <TableCell className='text-white border-l border-white w-[149px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('status')}</Box>
                {props.form.values.order_by?.type === 'asc' && props.form.values.order_by?.field === 'status' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('status', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('status', 'asc');
                    }}
                  />
                )}
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {props.alerts.map((row: any, index: number) => (
            <TableRow
              key={row.id}
              onClick={() => {
                if (row?.alert_lever === 3) {
                  props?.onDetail && props.onDetail({ id: row.id });
                }
              }}
              sx={{
                cursor: row?.alert_lever === 3 ? 'pointer' : 'default',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              <TableCell align='center'>{index + 1 + (props.form.values.page_index - 1) * props.pageSize}</TableCell>

              <TableCell align='center'>
                {row.alert_lever === 1 ? (
                  <Typography className='text-[#72777B]'>{t('alert_lever_options.notification')}</Typography>
                ) : row.alert_lever === 2 ? (
                  <Typography className='text-[#FF9500]'>{t('alert_lever_options.warning')}</Typography>
                ) : row.alert_lever === 3 ? (
                  <Typography className='text-[#ED1002]'>{t('alert_lever_options.critical')}</Typography>
                ) : (
                  row.alert_lever
                )}
              </TableCell>

              <TableCell align='center'>
                <Typography>{getAlertObject(row.alert_subject)}</Typography>
              </TableCell>
              <TableCell align='center'>{row.alert_object}</TableCell>
              <TableCell align='center'>{row.content}</TableCell>
              <TableCell align='center'>
                {row.created_at ? tzDayjs(row.created_at).format('DD/MM/YYYY - HH:mm') : ''}
              </TableCell>
              <TableCell align='center'>
                {row.alert_subject === 1 && row.status === 0 ? (
                  <Typography className='text-[#ED1002] font-bold whitespace-nowrap'>
                    {t('status_options.inProgress')}
                  </Typography>
                ) : row.status === 0 ? (
                  <Typography className='text-[#ED1002] whitespace-nowrap'>
                    {t('status_options.unProcessed')}
                  </Typography>
                ) : row.status === 1 || row.status === 2 || row.status === 3 ? (
                  <Typography className='text-[#3D9149] whitespace-nowrap'>{t('status_options.processed')}</Typography>
                ) : (
                  row.status
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default memo(AlertTable);
